<template>
  <div class="requests-page">
    <!-- Cabeçalho com Título, Filtros e Botão de Novo Pedido -->
    <header class="page-header">
      <h1>Client Requests</h1>

      <!-- Filtros de Pesquisa e Ordenação -->
      <div class="filter-form full-filter-bar">
        <!-- Status -->
        <div class="filter-wrapper">
          <label for="status-select" class="sort-label">Status:</label>
          <select id="status-select" v-model="status" @change="fetchRequests">
            <option value="all">All Requests</option>
            <option value="last_updated">Last Updates</option>
            <option value="pending">Pending</option>
            <option value="offer">Offer</option>
            <option value="counter-offer">Counter Offer</option>
            <option value="canceled">Canceled</option>
            <option value="accepted">Accepted</option>
            <option value="partially-sent">Partially Sent</option>
            <option value="sent">Sent</option>
          </select>
        </div>

        <!-- Type -->
        <div class="filter-wrapper">
          <label for="type-select" class="sort-label">Type:</label>
          <select id="type-select" v-model="type" @change="fetchRequests">
            <option value="all">All</option>
            <option value="normal">Normal</option>
            <option value="fba">FBA</option>
          </select>
        </div>

        <!-- SKU Input -->
        <div class="filter-wrapper">
          <label for="sku-input" class="sort-label">Search:</label>
          <input
            id="sku-input"
            type="text"
            v-model="search"
            placeholder="Search product"
            @keyup.enter="fetchRequests"
          />
        </div>

        <!-- Ordenação -->
        <div class="sort-controls">
          <!-- Sort By -->
          <div class="sort-wrapper">
            <label class="sort-label">Sort By</label>
            <select v-model="sortField" @change="fetchRequests">
              <option disabled value="">Select</option>
              <option value="id">ID</option>
              <option value="created_at">Date</option>
            </select>
          </div>

          <!-- Direction Button -->
          <div class="sort-wrapper direction">
            <label class="sort-label">Direction</label>
            <button class="sort-toggle-btn" @click="toggleDirection">
              <span>{{
                sortDirection === 'asc' ? 'Ascending' : 'Descending'
              }}</span>
              <i
                class="fas"
                :class="
                  sortDirection === 'asc' ? 'fa-arrow-up' : 'fa-arrow-down'
                "
              ></i>
            </button>
          </div>
        </div>

        <!-- Botões -->
        <div class="button-group">
          <button class="btn btn-filter" @click="fetchRequests">Filter</button>
          <button class="btn btn-success" @click="openSavedPricesPage">
            Saved Prices
          </button>
          <button class="btn btn-export-csv" @click="exportCSV">
            📄 Export CSV
          </button>
        </div>

        <!-- Taxa de Conversão EUR → AED -->
        <div class="filter-wrapper conversion-wrapper">
          <label for="exchange-rate-input" class="sort-label"
            >Taxa EUR→AED:</label
          >
          <input
            id="exchange-rate-input"
            type="number"
            step="0.0001"
            v-model.number="exchangeRate"
            @change="updateExchangeRate"
            placeholder="ex: 4.20"
          />
        </div>

        <div class="filter-wrapper conversion-wrapper">
          <label for="margin-input" class="sort-label">Margin:</label>
          <input
            id="margin-input"
            type="number"
            step="0.1"
            v-model.number="margin"
            @change="updateExchangeRate"
            placeholder="ex: 0.7"
          />
        </div>

        <div class="filter-wrapper conversion-wrapper">
          <label for="shipkg-input" class="sort-label">shippingPriceKg:</label>
          <input
            id="shipkg-input"
            type="number"
            step="0.1"
            v-model.number="shippingPriceKg"
            @change="updateExchangeRate"
            placeholder="ex: 10"
          />
        </div>
      </div>
    </header>

    <!-- Listagem dos Requests (Accordion) -->
    <section class="requests-list">
      <template v-if="loading">
        <div class="skeleton-card" v-for="n in 5" :key="n">
          <div class="skeleton-spinner"></div>
        </div>
      </template>
      <div v-else class="accordion-grid">
        <div v-for="req in requests" :key="req.id" class="accordion-item">
          <!-- Cabeçalho do Accordion -->
          <div class="accordion-header" @click.stop="toggleItem(req.id)">
            <div class="header-left">
              <!-- ID visível ANTES da imagem -->
              <div class="request-id">#{{ req.id }}</div>

              <a
                v-if="req.client_product && req.client_product.img"
                :href="req.client_product.img"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  :src="req.client_product.img"
                  alt="Product Image"
                  class="header-img"
                  @error="handleImageError"
                />
              </a>
              <div v-else class="no-image">No Image</div>

              <div class="summary">
                <h3 class="product-name">
                  <a
                    :href="`https://www.kuantokusta.pt/search?q=${encodeURIComponent(
                      req.client_product ? req.client_product.name : req.name
                    )}`"
                    target="_blank"
                    rel="noopener noreferrer"
                    style="color: inherit; text-decoration: none"
                  >
                    {{
                      (req.client_product
                        ? req.client_product.name
                        : req.name
                      ).slice(0, 75) +
                      ((req.client_product ? req.client_product.name : req.name)
                        .length > 75
                        ? '...'
                        : '')
                    }}
                  </a>
                  <span v-if="req.urgent" class="urgent-indicator">URGENT</span>
                </h3>
                <p>
                  <strong>SKU:</strong>
                  <template
                    v-if="req.client_product && req.client_product.prod_link"
                  >
                    <a
                      :href="req.client_product.prod_link"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="sku-link"
                    >
                      {{ req.client_product.sku }}
                    </a>
                  </template>
                  <template v-else>
                    {{ req.client_product ? req.client_product.sku : req.sku }}
                  </template>

                  <!-- Badge do tipo de encomenda -->
                  <span
                    v-if="req.type"
                    class="badge-type"
                    :class="{
                      'type-normal': req.type === 'normal',
                      'type-fba': req.type === 'fba',
                    }"
                  >
                    {{ req.type.toUpperCase() }}
                  </span>
                </p>
                <p>
                  Created: {{ formatDateTime(req.created_at) }}
                  <span
                    class="badge-age"
                    :class="getAgeClass(req.created_at, req)"
                    v-if="req.status !== 'sent'"
                  >
                    {{ getDaysPassed(req.created_at) }} dias
                  </span>

                  <span class="badge-age badge-neutral" v-else>
                    {{ getDaysBetween(req.created_at, req.updated_at) }} dias
                  </span>
                </p>
              </div>
            </div>

            <div
              v-if="
                req.associated_products &&
                req.associated_products.length &&
                !expandedItems[req.id]
              "
              class="header-middle"
            >
              <div
                v-for="(p, i) in req.associated_products"
                :key="i"
                class="assoc-box"
              >
                <div class="assoc-box-name">{{ p.name }}</div>
                <div class="assoc-box-skus">
                  {{ Array.isArray(p.sku) ? p.sku.join(', ') : p.sku }}
                </div>
              </div>
            </div>

            <div class="header-right">
              <span
                class="status-badge status-large"
                :class="statusClass(req.status)"
              >
                {{ req.status }}
              </span>
              <div class="header-actions" v-if="!expandedItems[req.id]">
                <template v-if="req.status === 'pending'">
                  <div class="offer-info bigger">
                    <div><strong>Order Qty:</strong> {{ req.qtyorder }} -</div>
                  </div>
                  <button
                    class="btn btn-primary"
                    @click.stop="openOfferModal(req)"
                  >
                    Offer
                  </button>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelModal(req.id)"
                  >
                    Cancel
                  </button>
                  <button
                    class="btn btn-warning"
                    style="margin-left: 3rem"
                    @click.stop="notifySlackCancelSourcing(req)"
                  >
                    Cancel & Keep Sourcing
                  </button>
                </template>
                <template v-else-if="req.status === 'offer'">
                  <div class="offer-info bigger">
                    <div>
                      <strong>Accepted Qty:</strong>
                      {{ req.qty_accepted || '-' }}
                    </div>
                    <div>
                      <strong>Price Offer:</strong> {{ req.price_offer || '-' }}
                    </div>
                  </div>
                  <button
                    class="btn btn-warning"
                    @click.stop="openOfferModal(req, true)"
                  >
                    Edit Offer
                  </button>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelModal(req.id)"
                  >
                    Cancel
                  </button>
                </template>
                <template v-else-if="req.status === 'counter-offer'">
                  <div class="offer-info bigger">
                    <div>
                      <strong>Accepted Qty:</strong>
                      {{ req.qty_accepted || '-' }}
                    </div>
                    <div>
                      <strong>Price Counter-Offer:</strong>
                      {{ req.price_counteroffer || '-' }}
                    </div>
                  </div>
                  <button
                    class="btn btn-success"
                    @click.stop="acceptOffer(req)"
                  >
                    Accept
                  </button>
                  <button
                    class="btn btn-warning"
                    @click.stop="openOfferModal(req)"
                  >
                    Offer
                  </button>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelModal(req.id)"
                  >
                    Cancel
                  </button>
                </template>
                <template v-else-if="req.status === 'accepted'">
                  <div class="offer-info bigger">
                    <div>
                      <strong>Accepted Qty:</strong>
                      {{ req.qty_accepted || '-' }}
                    </div>
                    <div>
                      <strong>Price Final:</strong>
                      {{ req.price_final || '-' }}
                    </div>
                  </div>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelAccepted(req.id)"
                  >
                    Cancel Request
                  </button>
                </template>
                <template v-else-if="req.status === 'partially-sent'">
                  <div class="offer-info bigger">
                    <div>
                      <strong>Accepted Qty:</strong>
                      {{ req.qty_accepted || '-' }}
                    </div>
                    <div>
                      <strong>Sent Qty:</strong> {{ req.qty_sent || '-' }}
                    </div>
                  </div>

                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelAccepted(req.id)"
                  >
                    Cancel Request
                  </button>
                </template>
              </div>
              <span
                class="message-notif"
                v-if="req.messages && req.messages.length"
              >
                {{ req.messages.length }}
              </span>
            </div>
          </div>

          <!-- Conteúdo Expandido do Accordion -->
          <transition name="accordion">
            <div
              v-show="expandedItems[req.id]"
              :key="req.id + '-content'"
              class="accordion-content"
            >
              <!-- Top Info -->
              <div class="top-info">
                <div class="info-block">
                  <h4>Date Info</h4>
                  <p>
                    <strong>Created:</strong>
                    {{ formatDateTime(req.created_at) }}
                  </p>
                  <p>
                    <strong>Updated:</strong>
                    {{ formatDateTime(req.updated_at) }}
                  </p>
                  <p><strong>Type:</strong> {{ req.type }}</p>
                </div>
                <div class="info-block">
                  <h4>Quantities</h4>
                  <p><strong>Order:</strong> {{ req.qty_order }}</p>
                  <p>
                    <strong>Accepted:</strong> {{ req.qty_accepted || '-' }}
                  </p>
                  <p><strong>Sent:</strong> {{ req.qty_sent || '-' }}</p>
                </div>
                <div class="info-block">
                  <h4>Pricing Info</h4>
                  <p><strong>Offer:</strong> {{ req.price_offer || '-' }}</p>
                  <p>
                    <strong>Counter:</strong>
                    {{ req.price_counteroffer || '-' }}
                  </p>
                  <p><strong>Final:</strong> {{ req.price_final || '-' }}</p>
                  <button
                    v-if="req.older_requests && req.older_requests.length"
                    class="btn btn-info btn-sm"
                    @click.stop="openOlderRequestsModal(req.older_requests)"
                  >
                    View Older Requests
                  </button>
                </div>
              </div>

              <div
                class="actions-row"
                v-if="
                  req.status === 'pending' ||
                  req.status === 'offer' ||
                  req.status === 'counter-offer' ||
                  req.status === 'accepted' ||
                  req.status === 'partially-sent'
                "
              >
                <!-- PENDING -->
                <template v-if="req.status === 'pending'">
                  <button
                    class="btn btn-primary"
                    @click.stop="openOfferModal(req)"
                  >
                    Offer
                  </button>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelModal(req.id)"
                  >
                    Cancel
                  </button>
                  <button
                    class="btn btn-warning"
                    style="margin-left: 3rem"
                    @click.stop="notifySlackCancelSourcing(req)"
                  >
                    Cancel & Keep Sourcing
                  </button>
                </template>

                <!-- OFFER -->
                <template v-else-if="req.status === 'offer'">
                  <button
                    class="btn btn-warning"
                    @click.stop="openOfferModal(req)"
                  >
                    Edit Offer
                  </button>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelModal(req.id)"
                  >
                    Cancel
                  </button>
                </template>

                <!-- COUNTER-OFFER -->
                <template v-else-if="req.status === 'counter-offer'">
                  <button
                    class="btn btn-primary"
                    @click.stop="openOfferModal(req)"
                  >
                    Offer
                  </button>
                  <button
                    class="btn btn-success"
                    @click.stop="acceptOffer(req)"
                  >
                    Accept
                  </button>
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelModal(req.id)"
                  >
                    Cancel
                  </button>
                </template>

                <!-- ACCEPTED -->
                <template v-else-if="req.status === 'accepted'">
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelAccepted(req.id)"
                  >
                    Cancel Request
                  </button>
                </template>

                <!-- PARTIALLY SENT -->
                <template v-else-if="req.status === 'partially-sent'">
                  <button
                    class="btn btn-danger"
                    @click.stop="openCancelAccepted(req.id)"
                  >
                    Cancel Request
                  </button>
                </template>
              </div>

              <!-- Shipments -->
              <div
                v-if="req.shipments && req.shipments.length"
                class="shipments-section"
              >
                <h4 @click.stop="toggleSection(req.id, 'shipments')">
                  Shipments
                  <i class="fa fa-truck shipment-icon"></i>
                  <span class="shipment-count"
                    >({{ req.shipments.length }})</span
                  >
                </h4>
                <div v-show="isSectionExpanded(req.id, 'shipments')">
                  <div class="shipment-cards">
                    <div
                      v-for="(shipment, idx) in req.shipments"
                      :key="idx"
                      class="shipment-card"
                    >
                      <p>
                        <strong>Order ID:</strong>
                        <a
                          :href="`/orders/${shipment.order_id}`"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="order-id-link"
                        >
                          {{ shipment.order_id }}
                        </a>
                      </p>
                      <p>
                        <strong>Quantity Sent:</strong> {{ shipment.qty_sent }}
                      </p>
                      <p><strong>SKU:</strong> {{ shipment.sku }}</p>
                      <p>
                        <strong>Tracking ID:</strong>
                        <a
                          v-if="shipment.tracking_id"
                          :href="`https://www.dhl.com/pt-en/home/tracking.html?tracking-id=${shipment.tracking_id}&submit=1`"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ shipment.tracking_id }}
                        </a>
                        <span v-else>-</span>
                      </p>
                      <p>
                        <strong>Sent At:</strong>
                        {{ formatDateTime(shipment.sent_at) }}
                      </p>
                      <p><strong>Exchange:</strong> {{ shipment.exchange }}</p>
                      <p>
                        <strong>Invoice:</strong>
                        <a
                          v-if="shipment.id_invoice"
                          href="#"
                          @click.prevent="openInvoice(shipment.id_invoice)"
                        >
                          {{ shipment.id_invoice }}
                        </a>
                        <span v-else>-</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mensagens -->
              <div class="messages-section">
                <h4 @click.stop="toggleSection(req.id, 'messages')">
                  Messages
                  <i
                    v-if="req.messages && req.messages.length"
                    class="fa fa-bell message-icon"
                  ></i>
                </h4>
                <div v-show="isSectionExpanded(req.id, 'messages')">
                  <div v-if="req.messages && req.messages.length">
                    <div
                      v-for="(msg, idx) in req.messages"
                      :key="idx"
                      class="chat-message"
                    >
                      <div class="chat-header">
                        <span class="chat-username">{{ msg.username }}</span>
                        <span class="chat-time">{{
                          formatDateTime(msg.created_at)
                        }}</span>
                      </div>
                      <div class="chat-body">{{ msg.message }}</div>
                    </div>
                  </div>
                  <div v-else>
                    <p>No messages</p>
                  </div>
                  <div class="send-message">
                    <textarea
                      v-model="newMessage"
                      placeholder="Type your message..."
                    ></textarea>
                    <button
                      class="btn btn-primary"
                      @click="sendMessage(req.id)"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>

              <!-- Produto e Associados -->
              <div class="product-section">
                <div class="product-info">
                  <h4>Product Info</h4>
                  <p>
                    <strong>Name:</strong>
                    <a
                      :href="`https://www.kuantokusta.pt/search?q=${encodeURIComponent(
                        req.client_product ? req.client_product.name : req.name
                      )}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="product-name-link"
                    >
                      {{
                        req.client_product ? req.client_product.name : req.name
                      }}
                    </a>
                  </p>
                  <p>
                    <strong>SKU:</strong>
                    {{ req.client_product ? req.client_product.sku : req.sku }}
                  </p>
                  <p class="link-truncate">
                    <strong>Link:</strong>
                    <template
                      v-if="req.client_product && req.client_product.prod_link"
                    >
                      <a
                        :href="req.client_product.prod_link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ req.client_product.prod_link }}
                      </a>
                    </template>
                    <template v-else>-</template>
                  </p>
                  <div class="product-img-wrapper">
                    <a
                      v-if="req.client_product && req.client_product.img"
                      :href="req.client_product.img"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        :src="req.client_product.img"
                        alt="Product Image"
                        class="product-img"
                        @error="handleImageError"
                      />
                    </a>
                  </div>
                </div>
                <div class="associated-products">
                  <h4>Associated Products</h4>
                  <div class="assoc-grid">
                    <div
                      v-for="(prod, index) in req.associated_products"
                      :key="index"
                      class="assoc-card clickable"
                      @click="openProductPage(prod)"
                    >
                      <a
                        v-if="prod.img"
                        :href="prod.img"
                        target="_blank"
                        rel="noopener noreferrer"
                        @click.stop
                      >
                        <img
                          :src="prod.img"
                          alt="Associated Product"
                          class="assoc-img"
                          @error="handleImageError"
                        />
                      </a>
                      <p><strong>SKU:</strong> {{ prod.sku.join(', ') }}</p>
                      <p><strong>Name:</strong> {{ prod.name }}</p>
                      <p><strong>Ratio:</strong> {{ prod.ratio }}</p>
                      <p>
                        <strong>Site Price:</strong>
                        {{ prod.site_price || '-' }}
                      </p>
                      <p><strong>Stock:</strong> {{ prod.stock || '-' }}</p>
                      <button
                        class="btn btn-danger btn-remove"
                        @click.stop="removeAssociation(prod, req)"
                      >
                        Remove
                      </button>
                    </div>
                    <!-- Card extra para adicionar associação -->
                    <div class="assoc-card assoc-add-card">
                      <button
                        class="btn btn-primary"
                        @click.stop="openAssociateModal(req)"
                      >
                        Add Product
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Logs -->
              <div class="logs-section">
                <h4 @click.stop="toggleSection(req.id, 'logs')">Logs</h4>
                <div v-show="isSectionExpanded(req.id, 'logs')">
                  <div
                    v-if="req.negotiation_logs && req.negotiation_logs.length"
                  >
                    <div
                      v-for="(log, idx) in req.negotiation_logs"
                      :key="idx"
                      class="log-card"
                    >
                      <div class="log-header">
                        <div class="log-user">
                          <strong>User:</strong> {{ log.username }}
                        </div>
                        <div class="log-date">
                          <strong>Date:</strong> {{ formatDateTime(log.date) }}
                        </div>
                      </div>
                      <div class="log-details">
                        <div><strong>Status:</strong> {{ log.status }}</div>
                        <div>
                          <strong>Value:</strong> {{ log.value || '-' }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <p>No logs</p>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </section>

    <!-- Modal para associação de produto -->
    <b-modal
      id="associateProductModal"
      v-model="showAssociateModal"
      title="Associate Product"
      hide-footer
      size="lg"
    >
      <form @submit.prevent="associateProduct" class="modal-form">
        <div class="row mb-4 align-items-start">
          <!-- Produto do Cliente -->
          <div class="col-md-6">
            <h5>Client Product</h5>
            <div class="product-box d-flex flex-column align-items-start">
              <a
                v-if="currentRequestForAssociation.client_product.img"
                :href="currentRequestForAssociation.client_product.img"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  :src="currentRequestForAssociation.client_product.img"
                  alt="Client Product Image"
                  class="product-image"
                  @error="handleImageError"
                />
              </a>
              <div v-else class="product-placeholder">No Image</div>

              <div class="mt-3 w-100">
                <p class="mb-1">
                  <strong>Name:</strong>
                  <span>{{
                    currentRequestForAssociation.client_product.name
                  }}</span>
                </p>
                <p class="mb-1">
                  <strong>SKU:</strong>

                  <a
                    v-if="currentRequestForAssociation.client_product.prod_link"
                    :href="
                      currentRequestForAssociation.client_product.prod_link
                    "
                    target="_blank"
                    rel="noopener noreferrer"
                    class="product-link"
                  >
                    {{ currentRequestForAssociation.client_product.sku }}
                  </a>
                  <span v-else>{{
                    currentRequestForAssociation.client_product.sku
                  }}</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Produto a Associar -->
          <div class="col-md-6">
            <h5>Product to Associate</h5>
            <div class="product-box d-flex flex-column align-items-start">
              <a
                v-if="prod_info && prod_info.img"
                :href="prod_info.img"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  :src="prod_info.img"
                  alt="Product Image"
                  class="product-image"
                  @error="handleImageError"
                />
              </a>
              <div v-else class="product-placeholder">No Image</div>

              <div class="mt-3 w-100">
                <div class="form-group mb-2">
                  <label>SKU</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="prod_info.sku"
                    disabled
                  />
                </div>
                <div class="form-group mb-2">
                  <label>EAN</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="prod_info.ean"
                    disabled
                  />
                </div>
                <div class="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    class="form-control"
                    v-model="prod_info.name"
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Campos de Pesquisa/Associação -->
        <div class="modal-form-group">
          <label>Product SKU</label>
          <input
            type="text"
            v-model="associateSKU"
            class="modal-form-control"
            @change="getInfoProduct"
            required
          />
        </div>
        <div class="modal-form-group">
          <label>Ratio</label>
          <input
            type="number"
            v-model.number="associateRatio"
            class="modal-form-control"
            required
          />
        </div>

        <div class="modal-actions">
          <button type="submit" class="btn btn-primary">Associate</button>
          <button
            type="button"
            class="btn btn-secondary"
            @click="closeAssociateModal"
          >
            Cancel
          </button>
        </div>
      </form>
    </b-modal>

    <!-- Modal para cancelamento -->
    <b-modal
      id="modal-cancel"
      v-model="showCancelModal"
      title="Cancel Request"
      hide-footer
    >
      <div class="input-group mb-3">
        <div class="input-group-prepend">
          <span class="input-group-text" id="reason-label">Reason</span>
        </div>
        <select class="form-control" v-model="cancelStatus">
          <option value="Not available">Not available</option>
          <option value="Impossible to ship">Impossible to ship</option>
          <option value="Descontinued - Remove from the marketplace">
            Not available (Descontinued - Remove from the marketplace)
          </option>
          <option value="Canceled by the customer">
            Canceled by the customer
          </option>
          <option
            value="Canceled - Due to exceeding the logical period to send to the customer"
          >
            Canceled - Due to exceeding the logical period to send to the
            customer
          </option>
        </select>
      </div>
      <div>
        <button class="btn btn-danger" @click="confirmCancelRequest">
          Cancel Request
        </button>
      </div>
    </b-modal>

    <!-- Modal para mostrar pedidos antigos -->
    <b-modal
      id="olderRequestsModal"
      v-model="showOlderRequestsModal"
      title="Older Requests"
      hide-footer
    >
      <div
        v-for="olderReq in currentOlderRequests"
        :key="olderReq.id"
        class="older-request"
      >
        <p>
          <strong>Accepted At:</strong>
          {{ formatDateTime(olderReq.created_at) }}
        </p>
        <p><strong>Final Price:</strong> {{ olderReq.price_final }}</p>
        <p>
          <strong> Qty:</strong>
          {{ olderReq.qty || '-' }}
        </p>
        <p>
          <strong>Type:</strong>
          <span class="badge badge-info text-uppercase">{{
            olderReq.type
          }}</span>
        </p>
      </div>
      <div class="text-center">
        <button
          class="btn btn-secondary"
          @click="showOlderRequestsModal = false"
        >
          Close
        </button>
      </div>
    </b-modal>

    <!-- Modal para fazer oferta -->
    <b-modal
      id="offerModal"
      v-model="showOfferModal"
      title="Make Offer"
      size="xl"
      hide-footer
      centered
      body-class="p-4 bg-light"
    >
      <div class="offer-grid">
        <!-- Product Info Card -->
        <div class="card info-card">
          <div class="card-header">
            <h5 class="card-title mb-0">Product Info</h5>
          </div>
          <div class="card-body d-flex align-items-center">
            <div class="avatar-container">
              <img
                v-if="currentRequestForOffer?.client_product?.img"
                :src="currentRequestForOffer.client_product.img"
                class="avatar"
                @error="handleImageError"
              />
              <div v-else class="avatar avatar-placeholder">?</div>
            </div>
            <div class="info-text ml-3">
              <h6 class="mb-1">
                <a
                  :href="`https://www.kuantokusta.pt/search?q=${encodeURIComponent(
                    currentRequestForOffer?.client_product?.name ||
                      currentRequestForOffer?.name
                  )}`"
                  target="_blank"
                  rel="noopener"
                  class="text-dark font-weight-bold"
                >
                  {{
                    currentRequestForOffer?.client_product?.name ||
                    currentRequestForOffer?.name
                  }}
                </a>
              </h6>
              <p class="mb-1 text-muted">
                SKU:
                <a
                  :href="currentRequestForOffer.client_product.prod_link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-monospace"
                  >{{
                    currentRequestForOffer.client_product?.sku ||
                    currentRequestForOffer?.sku
                  }}
                </a>
              </p>
              <p class="mb-0">
                Type:
                <span class="badge badge-info text-uppercase">{{
                  currentRequestForOffer?.type
                }}</span>
                <span
                  v-if="currentRequestForOffer?.urgent"
                  class="badge badge-danger ml-1"
                  >URGENT</span
                >
              </p>
            </div>
          </div>
        </div>

        <!-- Associated Products Card -->
        <div class="card assoc-card">
          <div class="card-header">
            <h5 class="card-title mb-0">Associated Products</h5>
          </div>
          <div class="card-body assoc-body">
            <div
              v-if="currentRequestForOffer.associated_products?.length"
              class="assoc-row"
            >
              <div
                v-for="(
                  prod, idx
                ) in currentRequestForOffer.associated_products"
                :key="idx"
                class="assoc-item card p-2 d-flex align-items-center"
                @click="openProductPage(prod)"
              >
                <img
                  v-if="prod.img"
                  :src="prod.img"
                  class="assoc-thumb"
                  @error="handleImageError"
                />
                <div class="ml-2 flex-grow-1">
                  <p class="mb-0 font-weight-bold small">{{ prod.name }}</p>
                  <p class="mb-0 text-muted xsmall">
                    <strong>SKU:</strong> {{ prod.sku.join(', ') }}
                  </p>
                  <p class="mb-0 text-muted xsmall">
                    <strong>Ratio:</strong> {{ prod.ratio }}
                  </p>
                  <p class="mb-0 text-muted xsmall">
                    <strong>Site Price:</strong> {{ prod.site_price || '-' }}
                  </p>
                  <p class="mb-0 text-muted xsmall">
                    <strong>Stock:</strong> {{ prod.stock || '-' }}
                  </p>
                </div>
                <button
                  class="btn btn-link text-danger p-0"
                  @click.stop="removeAssociation(prod, currentRequestForOffer)"
                  title="Remove"
                >
                  <i class="fas fa-times-circle"></i>
                </button>
              </div>
              <button
                class="btn btn-outline-primary btn-sm mx-2"
                @click.stop="openAssociateModal(currentRequestForOffer)"
              >
                + Add
              </button>
            </div>
            <div v-else class="text-muted text-center small">
              <div class="mt-3">No associated products.</div>
              <small
                v-if="currentRequestForOffer.associated_products.length === 0"
                class="text-danger"
              >
                Adicione pelo menos um produto associado antes de submeter.
              </small>
              <button
                class="btn btn-outline-primary btn-sm mx-2"
                @click.stop="openAssociateModal(currentRequestForOffer)"
              >
                + Add
              </button>
            </div>
          </div>
        </div>

        <!-- Older Offers Card (full width) -->
        <div class="card full-card">
          <div class="card-header">
            <h5 class="card-title mb-0">Older Offers</h5>
          </div>
          <div class="card-body d-flex overflow-auto">
            <div
              v-for="(older, idx) in currentRequestForOffer.older_requests"
              :key="idx"
              class="older-item text-center px-3 py-2 mr-2"
            >
              <p class="mb-1 text-small text-muted">
                {{ formatDateTime(older.created_at) }}
              </p>
              <p class="mb-0 font-weight-bold">{{ older.price_final }} AED</p>
              <p class="mb-0 text-muted small">Qty: {{ older.qty || '-' }}</p>
              <p class="mb-0 text-muted small">
                Type:
                <span class="badge badge-info">{{
                  older.type.toUpperCase()
                }}</span>
              </p>
            </div>
            <p
              v-if="!currentRequestForOffer.older_requests?.length"
              class="text-muted small"
            >
              No older offers.
            </p>
          </div>
        </div>

        <!-- Offer Details & Calculator Card (full width) -->
        <div class="card full-card">
          <div class="card-body">
            <div class="row">
              <!-- Details -->
              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="small font-weight-bold">Qty Ordered</label>
                  <p class="font-monospace">
                    {{ currentRequestForOffer?.qty_order }}
                  </p>
                </div>
                <div class="form-group">
                  <label class="small font-weight-bold">Accepted Qty</label>
                  <b-form-input
                    type="number"
                    v-model.number="offerForm.qty"
                    size="sm"
                    class="form-control"
                  />
                </div>
                <div
                  v-if="
                    currentRequestForOffer.price_counteroffer !== undefined &&
                    currentRequestForOffer.price_counteroffer !== null
                  "
                  class="form-group"
                >
                  <div class="row">
                    <!-- Coluna do Price Offer -->
                    <div class="col-sm-6">
                      <label class="small font-weight-bold"
                        >Price Offer (AED)</label
                      >
                      <p class="font-monospace mb-0">
                        {{ currentRequestForOffer.price_offer }}
                      </p>
                    </div>
                    <!-- Coluna do Price Counter Offer -->
                    <div class="col-sm-6">
                      <label class="small font-weight-bold"
                        >Price Counter Offer (AED)</label
                      >
                      <p class="font-monospace mb-0">
                        {{ currentRequestForOffer.price_counteroffer }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label class="small font-weight-bold"
                    >Price Offer (AED)</label
                  >
                  <b-form-input
                    type="number"
                    v-model.number="offerForm.price"
                    size="sm"
                    class="form-control"
                  />
                  <small v-if="offerForm.price <= 0" class="text-danger">
                    Insira um preço válido (> 0).
                  </small>
                </div>
              </div>

              <!-- Calculadoras (direita) -->
              <div class="col-md-6 mb-3">
                <!-- Calculadora 1: EUR → AED -->
                <label class="small font-weight-bold">Convert EUR → AED</label>
                <div class="input-group input-group-sm mb-2">
                  <input
                    type="number"
                    v-model.number="calcAmount"
                    class="form-control"
                    placeholder="EUR"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">→</span>
                  </div>
                  <input
                    type="text"
                    v-model="convertedAED"
                    readonly
                    class="form-control"
                    placeholder="AED"
                  />
                  <div class="input-group-append">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      @click="copyToOffer"
                    >
                      Copy
                    </b-button>
                  </div>
                </div>
                <small class="text-muted"
                  >Rate: {{ exchangeRate }} AED/EUR</small
                >

                <!-- Separador visual -->
                <hr />
                <label class="small font-weight-bold">Calculator</label>
                <!-- Calculadora 2: Custo (€) + Peso → Total em AED -->
                <div class="mb-2">
                  <b-form-checkbox v-model="withIVA" switch size="sm">
                    IVA
                  </b-form-checkbox>
                </div>
                <div class="input-group input-group-sm mb-2">
                  <!-- Add with iva and no iva slider to turn on and off -->
                  <input
                    type="number"
                    v-model.number="costPrice"
                    class="form-control"
                    placeholder="EUR"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">+</span>
                  </div>
                  <input
                    type="number"
                    v-model.number="shippingValue"
                    readonly
                    class="form-control"
                    placeholder="EUR"
                  />
                  <div class="input-group-append">
                    <span class="input-group-text">=</span>
                  </div>
                  <input
                    type="text"
                    v-model="convertedAED2"
                    readonly
                    class="form-control"
                    placeholder="AED"
                  />

                  <div class="input-group-append">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      @click="copyToOffer2"
                    >
                      Copy
                    </b-button>
                  </div>
                </div>
                <small class="text-muted"
                  >Rate: {{ exchangeRate }} AED/EUR
                </small>
                <small class="text-muted"> || </small>
                <small class="text-muted">Weight: {{ totalWeight }} g</small>
              </div>
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <!-- Botão à esquerda -->
              <div>
                <b-button
                  variant="success"
                  :disabled="!canSubmitOffer"
                  @click="savePrice"
                  :title="
                    canSubmitOffer
                      ? ''
                      : 'É preciso definir preço e adicionar pelo menos 1 produto associado'
                  "
                >
                  Save Price
                </b-button>
              </div>

              <!-- Botões à direita -->
              <div>
                <b-button
                  variant="primary"
                  :disabled="!canSubmitOffer"
                  @click="makeOffer"
                  :title="
                    canSubmitOffer
                      ? ''
                      : 'É preciso definir preço e adicionar pelo menos 1 produto associado'
                  "
                >
                  Submit Offer
                </b-button>

                <b-button
                  variant="secondary"
                  class="ml-2"
                  @click="closeOfferModal"
                >
                  Cancel
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { BModal } from 'bootstrap-vue';
import Swal from 'sweetalert2';
import _ from 'lodash';
import axios from 'axios';

export default {
  components: { BModal },
  data() {
    return {
      requests: [],
      loading: false,
      searchQuery: '',
      status: 'pending',
      type: 'all',
      search: '',
      // Campos de ordenação
      sortField: 'id',
      sortDirection: 'desc',
      // Controle do accordion
      expandedItems: {},
      expandedSections: {},
      showCreateModal: false,
      newMessage: '',
      // Dados para modal de associação de produto
      showAssociateModal: false,
      associateSKU: '',
      associateRatio: null,
      currentRequestForAssociation: {
        client_product: {
          id: null,
          name: '',
          sku: '',
          img: '',
          prod_link: '',
        },
        associated_products: [],
      },
      prod_info: {
        name: '',
        sku: '',
        ean: '',
        img: '',
      },
      // Dados para modal de oferta
      showOfferModal: false,
      currentRequestForOffer: {
        id: null,
        client_id: null,
        client_product: {
          id: null,
          name: '',
          sku: '',
          img: '',
          prod_link: '',
        },
        associated_products: [],
        older_requests: [],
        qty_order: 0,
        qty_accepted: 0,
        price_offer: null,
        price_counteroffer: null,
        price_final: null,
      },
      //Dados para pedidos antigos
      showOlderRequestsModal: false,
      currentOlderRequests: [],
      // Dados para modal de cancelamento
      showCancelModal: false,
      cancelStatus: 'Not available',
      currentCancelId: null,
      offerForm: {
        qty: 1,
        price: null,
      },
      //Dados para a calculadora
      exchangeRate: 4.18,
      calcAmount: 0,
      //Dados para a calculadora de custo
      costPrice: 0,
      totalWeight: 0,
      iva: 0.23, // IVA de 23%
      shippingValue: 0,
      withIVA: true,
      margin: 0.7, // Margem de lucro (30%)
      shippingPriceKg: 10, // Preço de envio
    };
  },
  methods: {
    fetchRequests: _.debounce(async function () {
      try {
        this.loading = true;
        console.log(this.status);
        console.log(this.search);
        console.log(this.sortField);
        console.log(this.sortDirection);
        await this.$store.dispatch('getRequestsClientAdmin', {
          status: this.status,
          type: this.type,
          search: this.search,
          sort_by: this.sortField,
          sort_dir: this.sortDirection,
        });
        this.requests = this.$store.getters.getRequestsClient;
        console.log('Fetched requests:', this.requests);
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    }, 300), // 300ms de debounce
    toggleDirection() {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      this.fetchRequests();
    },
    // Função para abrir/fechar o accordion
    toggleItem(id) {
      if (this.expandedItems[id]) {
        this.$delete(this.expandedItems, id);
      } else {
        this.expandedItems = {};
        this.$set(this.expandedItems, id, true);
        if (!this.expandedSections[id]) {
          this.$set(this.expandedSections, id, {
            messages: false,
            logs: false,
            shipments: false,
          });
        }
      }
    },
    toggleSection(reqId, section) {
      if (!this.expandedSections[reqId]) {
        this.$set(this.expandedSections, reqId, {
          messages: false,
          logs: false,
          shipments: false,
        });
      }
      this.expandedSections[reqId][section] =
        !this.expandedSections[reqId][section];
    },
    isSectionExpanded(reqId, section) {
      return this.expandedSections[reqId]
        ? this.expandedSections[reqId][section]
        : false;
    },
    async confirmCancelRequest() {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Cancel this request?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it!',
      });
      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('cancelRequestsClient', {
            id: this.currentCancelId,
            cancel_status: this.cancelStatus,
          });
          //Fechar modal
          this.showCancelModal = false;
          this.cancelStatus = 'Not available';
          await Swal.fire('Canceled!', 'Request has been canceled.', 'success');
          this.fetchRequests();
        } catch (error) {
          await Swal.fire('Error', error.message, 'error');
        }
      }
    },
    async acceptOffer(req) {
      const result = await Swal.fire({
        title: `Accept Offer for ${
          req.client_product ? req.client_product.sku : req.sku
        }?`,
        text: 'This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, accept!',
      });
      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('acceptRequestsClient', { id: req.id });
          await Swal.fire(
            'Accepted!',
            'Offer accepted successfully.',
            'success'
          );
          this.fetchRequests();
        } catch (error) {
          await Swal.fire('Error', error.message, 'error');
        }
      }
    },
    async makeOffer() {
      if (!this.canSubmitOffer) {
        alert('Please set a price and add at least one associated product.');
        return;
      }
      try {
        await this.$store.dispatch('offerRequestClient', {
          id: this.currentRequestForOffer.id,
          qty_accepted: this.offerForm.qty,
          price_offer: this.offerForm.price,
        });
        await Swal.fire('Success', 'Offer updated successfully!', 'success');
        this.fetchRequests();
        this.showOfferModal = false;
        // Reset form
        this.offerForm.qty = 1;
        this.offerForm.price = null;
      } catch (error) {
        await Swal.fire('Error', error.message, 'error');
      }
    },
    async savePrice() {
      if (!this.offerForm.price || this.offerForm.price <= 0) {
        await Swal.fire(
          'Invalid Price',
          'Please enter a valid price greater than 0.',
          'error'
        );
        return;
      }
      const { data, status } = await axios.post(
        `${this.$store.state.apiUrl}clientRequest/autoPrice/${this.currentRequestForOffer.client_id}`,
        {
          request_id: this.currentRequestForOffer.id,
          price_offer: this.offerForm.price,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.$store.state.token}`,
          },
          validateStatus: (statusCode) => {
            // Retorna true para qualquer status entre 200 e 499
            return statusCode >= 200 && statusCode < 500;
          },
        }
      );
      console.log('Response data:', data);
      console.log('Response status:', status);
      if (status >= 200 && status < 300) {
        if (data.status == 'success') {
          await Swal.fire('Success', 'Price saved successfully!', 'success');
          this.fetchRequests();
        }
      } else {
        if (data.status == 'found') {
          await Swal.fire(
            'Price Found',
            `Price found: ${data.data.price_uni} AED -> Exchange Rate: ${data.data.exchange} AED/EUR -> Valid until: ${data.data.valid_until}`,
            'info'
          );
        } else {
          console.error('Failed to fetch exchange rate:', data.message);
          Swal.fire('Error', 'Failed to fetch exchange rate.', 'error');
        }
      }
    },
    async sendMessage(reqId) {
      if (!this.newMessage) return;
      try {
        await this.$store.dispatch('sendMessageRequest', {
          id: reqId,
          message: this.newMessage,
          username: JSON.parse(localStorage.getItem('user')).username,
        });
        await Swal.fire('Success', 'Message sent!', 'success');
        this.newMessage = '';
        this.fetchRequests();
      } catch (error) {
        await Swal.fire('Error', error.message, 'error');
      }
    },
    handleImageError(e) {
      e.target.src =
        'https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg';
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
    },
    statusClass(st) {
      switch (st) {
        case 'pending':
          return 'status-pending';
        case 'offer':
          return 'status-offer';
        case 'counter-offer':
          return 'status-counter';
        case 'canceled':
          return 'status-canceled';
        case 'accepted':
          return 'status-accepted';
        case 'partially-sent':
          return 'status-partially-sent';
        case 'sent':
          return 'status-sent';
        default:
          return 'status-default';
      }
    },
    getDaysPassed(date) {
      const created = new Date(date);
      const now = new Date();
      const diffMs = now - created;
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    },
    getDaysBetween(start, end) {
      const d1 = new Date(start);
      const d2 = new Date(end);
      const diffMs = d2 - d1;
      return Math.floor(diffMs / (1000 * 60 * 60 * 24));
    },
    formatDaysLabel(days, hours) {
      if (hours < 24) return '< 1 dia';
      return days === 1 ? '1 dia' : `${days} dias`;
    },
    getAgeClass(date, req) {
      if (req.status === 'sent') return 'badge-neutral';

      const created = new Date(date);
      const now = new Date();
      const diffMs = now - created;
      const hours = diffMs / (1000 * 60 * 60);
      const days = Math.floor(hours / 24);

      if (hours < 24) return 'age-fresh'; // nova cor para <1 dia
      if (days <= 2) return 'age-purple';
      if (days === 3) return 'age-yellow';
      if (days === 4) return 'age-orange';
      if (days === 5) return 'age-red';
      return 'age-black';
    },
    // Modal para associar produto
    openAssociateModal(req) {
      this.currentRequestForAssociation = req;
      this.showAssociateModal = true;
    },
    closeAssociateModal() {
      this.showAssociateModal = false;
      this.associateSKU = '';
      this.associateRatio = null;
      this.currentRequestForAssociation = {
        client_product: {
          id: null,
          name: '',
          sku: '',
          img: '',
          prod_link: '',
        },
        associated_products: [],
      };
      this.prod_info = {
        name: '',
        sku: '',
        ean: '',
        img: '',
      };
    },
    async associateProduct() {
      try {
        await this.$store.dispatch('associateClientProduct', {
          client_prod_id: this.currentRequestForAssociation.client_product.id,
          sku: this.associateSKU,
          ratio: this.associateRatio,
        });
        await Swal.fire('Success', 'Product associated!', 'success');
      } catch (error) {
        await Swal.fire('Error', error.message, 'error');
      }
      this.closeAssociateModal();
      await this.fetchRequests();
      // Update currentRequestForOffer with the updated info from the fetched requests.
      // This ensures that the modal now shows the most current associated products, etc.
      if (!this.currentRequestForOffer) return;
      const updatedRequest = this.requests.find(
        (req) => req.id === this.currentRequestForOffer.id
      );
      if (updatedRequest) {
        this.currentRequestForOffer = updatedRequest;
      }
    },
    async removeAssociation(prod, req) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'Do you want to remove this association?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, remove it!',
      });
      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('removeAssoClientProduct', {
            prod_id: prod.prod_id,
            client_prod_id: req.client_product.id,
          });
          await Swal.fire(
            'Removed!',
            'Association removed successfully.',
            'success'
          );
          await this.fetchRequests();
          // Update currentRequestForOffer with the updated info from the fetched requests.
          // This ensures that the modal now shows the most current associated products, etc.
          const updatedRequest = this.requests.find(
            (req) => req.id === this.currentRequestForOffer.id
          );
          if (updatedRequest) {
            this.currentRequestForOffer = updatedRequest;
          }
        } catch (error) {
          await Swal.fire('Error', error.message, 'error');
        }
      }
    },
    async getInfoProduct() {
      await this.$store.dispatch('searchProductsEanSku', {
        search: this.associateSKU,
      });

      const prod_info_2 = this.$store.getters.getProductsSkuEan;
      console.log('Product info:', prod_info_2);

      this.prod_info = {
        name: prod_info_2[0].name || '',
        sku: prod_info_2[0].sku[0] || '',
        ean: prod_info_2[0].ean[0] || '',
        img: prod_info_2[0].img || '',
      };

      if (this.prod_info.sku && this.prod_info.sku != this.associateSKU) {
        this.associateSKU = this.prod_info.sku;
      }
    },
    //Modal para cancelar pedido
    openCancelModal(id) {
      this.currentCancelId = id;
      this.showCancelModal = true;
    },
    //Modal para pedidos antigos
    openOlderRequestsModal(olderRequests) {
      this.currentOlderRequests = olderRequests;
      this.showOlderRequestsModal = true;
    },
    //Modal para fazer oferta
    openOfferModal(req, edit = false) {
      this.currentRequestForOffer = req;
      console.log('Opening offer modal for:', this.currentRequestForOffer);
      this.showOfferModal = true;
      this.offerForm.qty = req.qty_order;
      this.offerForm.price = null;
      if (edit) {
        this.offerForm.price = req.price_offer;
        this.offerForm.qty = req.qty_accepted;
      }
      this.costPrice = 0;
      this.totalWeight = 0;
      this.shippingValue = 0;
      this.iva = 1.23; // Default to 23% if no products have IVA

      //Insert the calcAmount
      //Loop through the associated products to find the value in EUR
      this.calcAmount = 0;
      if (req.associated_products && req.associated_products.length > 0) {
        req.associated_products.forEach((prod) => {
          if (prod.site_price) {
            this.calcAmount +=
              parseFloat(prod.site_price) * parseInt(prod.ratio);
          }
        });
      } else {
        this.calcAmount = 0;
      }

      //Calc total weight and total
      if (req.associated_products && req.associated_products.length > 0) {
        req.associated_products.forEach((prod) => {
          if (prod.weight) {
            this.totalWeight += parseFloat(prod.weight) * parseInt(prod.ratio);
          }
        });
      } else {
        this.totalWeight = 0;
      }

      this.shippingValue = (
        (Math.ceil(this.totalWeight / 100) / 10) *
        this.shippingPriceKg
      ).toFixed(2);

      //Calc IVA
      if (req.associated_products && req.associated_products.length > 0) {
        //I want to loop through the associated products and get the higher iva value
        let maxIva = 0;
        req.associated_products.forEach((prod) => {
          if (prod.iva && parseFloat(prod.iva) > maxIva) {
            maxIva = parseFloat(prod.iva);
          }
        });
        this.iva = 1 + maxIva / 100 || 1.23; // Default to 23% if no products have IVA
      } else {
        this.iva = 1.23; // Default to 23% if no products have IVA
      }
    },
    closeOfferModal() {
      this.showOfferModal = false;
      this.offerForm.qty = 1;
      this.offerForm.price = null;
      this.currentRequestForOffer = {
        id: null,
        client_product: {
          id: null,
          name: '',
          sku: '',
          img: '',
          prod_link: '',
        },
        associated_products: [],
        older_requests: [],
        qty_order: 0,
        qty_accepted: 0,
        price_offer: null,
        price_counteroffer: null,
        price_final: null,
      };
      this.costPrice = 0;
    },
    // Abrir página do produto associado
    openProductPage(prod) {
      // Exemplo: redireciona para uma página de detalhes do produto
      // Você pode ajustar a URL conforme necessário ou usar $router.push se estiver usando Vue Router.
      localStorage.setItem('product', JSON.stringify(prod.sku[0]));
      window.open(`https://${window.location.host}/about`, '_blank');
    },
    exportCSV() {
      const rows = [];

      const headers = [
        'Request ID',
        'SKU',
        'Product Name',
        'Status',
        'Qty Order',
        'Qty Accepted',
        'Qty Sent',
        'Price Offer',
        'Price Counteroffer',
        'Price Final',
        'Type',
        'Urgent',
        'Created At',
        'Updated At',
        'Shipment SKU',
        'Shipment Qty',
        'Tracking ID',
        'Sent At',
      ];

      this.requests.forEach((req) => {
        const baseRow = [
          req.id || '',
          req.client_product?.sku || req.sku || '',
          req.client_product?.name || req.name || '',
          req.status || '',
          req.qty_order || '',
          req.qty_accepted || '',
          req.qty_sent || '',
          req.price_offer || '',
          req.price_counteroffer || '',
          req.price_final || '',
          req.type || '',
          req.urgent ? 'Yes' : 'No',
          this.formatDateTime(req.created_at),
          this.formatDateTime(req.updated_at),
        ];

        // Garante que shipments é array, mesmo que reactive, undefined ou null
        const shipments = Array.isArray(req.shipments)
          ? req.shipments
          : req.shipments
          ? JSON.parse(JSON.stringify(req.shipments))
          : [];

        if (shipments.length > 0) {
          shipments.forEach((sh) => {
            const shipmentRow = [
              sh.sku || '',
              sh.qty_sent || '',
              sh.tracking_id || '-',
              this.formatDateTime(sh.sent_at),
            ];
            rows.push([...baseRow, ...shipmentRow]);
          });
        } else {
          rows.push([...baseRow, '', '', '', '']);
        }
      });

      // Cria CSV com BOM + vírgulas + aspas em todas as colunas
      const csvBody = [headers, ...rows]
        .map((row) =>
          row
            .map((cell) => `"${(cell ?? '').toString().replace(/"/g, '""')}"`)
            .join(',')
        )
        .join('\n');

      const BOM = '\uFEFF'; // UTF-8 BOM para Excel
      const csvContent = BOM + csvBody;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute(
        'download',
        `client_requests_shipments_${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    //Calculadora de câmbio
    async fetchExchangeRate() {
      try {
        const { data } = await axios.get(
          `${this.$store.state.apiUrl}clientRequest/exchange/AED`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
          }
        );

        if (data?.status === 'success') {
          this.exchangeRate = Number(data.data.exchange) || 0;
          this.margin = Number(data.data.margin) || 0;
          this.shippingPriceKg = Number(data.data.price_kg) || 0;
        } else {
          console.error('Failed to fetch:', data?.message);
          Swal.fire('Error', 'Failed to fetch settings.', 'error');
        }
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Request failed (network/server).', 'error');
      }
    },
    async updateExchangeRate() {
      try {
        console.log('Updated:', {
          exchangeRate: this.exchangeRate,
          margin: this.margin,
          shippingPriceKg: this.shippingPriceKg,
        });

        const { data } = await axios.patch(
          `${this.$store.state.apiUrl}clientRequest/exchange/AED`,
          {
            exchange: this.exchangeRate,
            margin: this.margin,
            shippingPriceKg: this.shippingPriceKg,
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
          }
        );

        if (data?.status === 'success') {
          Swal.fire('Success', 'Settings updated successfully!', 'success');
        } else {
          Swal.fire(
            'Error',
            data?.message || 'Failed to update settings.',
            'error'
          );
        }
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Request failed (network/server).', 'error');
      }
    },
    copyToOffer() {
      if (this.calcAmount && this.exchangeRate) {
        this.offerForm.price = this.convertedAED;
      } else {
        Swal.fire('Error', 'Please enter a valid amount in EUR.', 'error');
      }
    },
    copyToOffer2() {
      if (this.convertedAED2 != 0.0 && this.exchangeRate) {
        this.offerForm.price = this.convertedAED2;
      } else {
        Swal.fire('Error', 'Please enter a valid amount in EUR.', 'error');
      }
    },
    //Função para abrir a página dos produtos com preços guardados
    openSavedPricesPage() {
      window.open(
        `https://${window.location.host}/client/savedPrices`,
        '_blank'
      );
    },
    async openInvoice(id) {
      try {
        const link = await this.$store.dispatch('fetchPDFLink', { id });
        if (link) {
          window.open(link, '_blank');
        }
      } catch (err) {
        console.error(err);
        this.$swal({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: 'Erro ao abrir fatura',
          showConfirmButton: false,
          timer: 2000,
        });
      }
    },
    //Função para cancelar a oferta depois de aceite ou enviada
    openCancelAccepted(id) {
      Swal.fire({
        title: 'Cancel Request?',
        text: "This will remove the product from the client's virtual order.",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it',
        cancelButtonText: 'No',
      }).then((result) => {
        if (result.isConfirmed) {
          this.cancelAfterAcceptance(id);
        }
      });
    },
    async cancelAfterAcceptance(id) {
      try {
        const { data } = await axios.post(
          `${this.$store.state.apiUrl}clientRequest/requests/cancelAdmin`,
          { id },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
          }
        );

        if (data.status === 'success') {
          await Swal.fire(
            'Cancelled!',
            'Request has been cancelled.',
            'success'
          );
          this.fetchRequests();
        } else {
          Swal.fire('Error', data.message || 'Something went wrong.', 'error');
        }
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Something went wrong.', 'error');
      }
    },
    async notifySlackCancelSourcing(req) {
      const result = await Swal.fire({
        title: 'Send Slack notification?',
        text: `This will notify Slack that request #${req.id} should be cancelled on the client's side, while we keep trying to source the product.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, notify Slack',
      });

      if (!result.isConfirmed) return;

      try {
        await axios.post(
          `${this.$store.state.apiUrl}clientRequest/requests/notify-slack`,
          { id: req.id },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.$store.state.token}`,
            },
          }
        );
        await Swal.fire(
          'Sent!',
          'Slack notification sent successfully.',
          'success'
        );
      } catch (error) {
        await Swal.fire('Error', error.message, 'error');
      }
    },
  },
  async created() {
    await this.fetchRequests();
    await this.fetchExchangeRate();
  },
  computed: {
    convertedAED() {
      if (this.calcAmount && this.exchangeRate) {
        // multiply and always round up to the next integer
        return Math.ceil(this.calcAmount * this.exchangeRate);
      }
      return '0.00';
    },
    convertedAED2() {
      if (
        this.costPrice &&
        this.margin &&
        this.shippingValue &&
        this.exchangeRate
      ) {
        // multiply and always round up to the next integer
        const total_eur = this.withIVA
          ? Number(this.costPrice) / Number(this.iva) / Number(this.margin) +
            Number(this.shippingValue)
          : Number(this.costPrice) / Number(this.margin) +
            Number(this.shippingValue);

        const total = Number(total_eur) * this.exchangeRate;
        return Math.ceil(total);
      }
      return '0.00';
    },
    canSubmitOffer() {
      const priceOk = Number(this.offerForm.price) > 0;
      const hasAssoc =
        Array.isArray(this.currentRequestForOffer.associated_products) &&
        this.currentRequestForOffer.associated_products.length > 0;
      return priceOk && hasAssoc;
    },
  },
};
</script>

<style scoped>
.swal2-container {
  z-index: 3000 !important;
}
.requests-page {
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
  font-family: 'Poppins', sans-serif;
  color: #333;
}
.page-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 20px;
}
.page-header h1 {
  font-size: 32px;
  margin-bottom: 10px;
}
.filter-form {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 10px;
}
.filter-form select,
.filter-form input {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}
.filter-form label {
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px;
}
.btn.btn-filter {
  background-color: #3498db;
  color: #fff;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.btn.btn-filter:hover {
  background-color: #2980b9;
}
.btn-export-csv {
  background-color: #f39c12;
  color: #fff;
  padding: 8px 14px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: background-color 0.2s ease;
}

.btn-export-csv:hover {
  background-color: #d68910;
}
.btn.btn-add-request {
  background-color: #2ecc71;
  color: #fff;
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 10px;
}
.btn.btn-add-request:hover {
  background-color: #27ae60;
}
/* b-modal */
.modal-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.modal-form-group {
  display: flex;
  flex-direction: column;
}
.modal-form-control {
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.modal-actions {
  text-align: center;
  margin-top: 20px;
}
.modal-actions .btn {
  margin: 0 10px;
}
/* Accordion Layout */
.accordion-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  margin-top: 10px;
  width: 100%;
}
.accordion-item {
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}
.accordion-header {
  display: flex;
  /*justify-content: space-between;*/
  gap: 1rem;
  align-items: center;
  padding: 14px 16px;
  background: #f7f7f7;
  cursor: pointer;
  transition: background 0.2s ease;
}
.accordion-header:hover {
  background: #f1f1f1;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-img {
  width: 50px;
  height: 50px;
  object-fit: contain;
}
.summary {
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.product-name {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #333;
}
.summary p {
  margin: 2px 0 0;
  font-size: 12px;
  color: #666;
}
/* A coluna intermédia some quando expandido e permite scroll se necessário */
.header-middle {
  flex: 0 1 auto;
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding: 4px 0;
}

/* Caixinhas com sombra e cantos arredondados */
.assoc-box {
  min-width: 120px;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 6px 10px;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.05);
  transition: background 0.2s;
}
.assoc-box:hover {
  background: #e8e8e8;
}

.assoc-box-name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.assoc-box-skus {
  font-size: 0.75rem;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  margin-left: auto;
}
.status-badge.status-large {
  font-size: 16px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 16px;
  margin-right: 10px;
}
.status-badge {
  text-transform: uppercase;
  color: #fff;
  background: #7f8c8d;
}
.status-pending {
  background-color: #f1c40f;
}
.status-offer {
  background-color: #8e44ad;
}
.status-counter {
  background-color: #e67e22;
}
.status-canceled {
  background-color: #c0392b;
}
.status-accepted {
  background-color: #27ae60;
}
.status-sent {
  background-color: #2980b9;
}
.status-partially-sent {
  background-color: #f39c12; /* laranja tipo warning */
}
.status-default {
  background-color: #7f8c8d;
}
.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}
.offer-info.bigger {
  font-size: 14px;
  font-weight: 600;
  color: #444;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.offer-info.bigger div {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 2px 6px;
}
.message-notif {
  background: #e74c3c;
  color: #fff;
  padding: 4px 8px;
  border-radius: 50%;
  font-size: 12px;
}
.accordion-content {
  padding: 16px;
  background: #fff;
  transition: all 0.3s ease;
}
.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
}
.accordion-enter,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
.top-info {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}
.info-block {
  flex: 1;
  background: #fafafa;
  padding: 10px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #eee;
}
.info-block h4 {
  margin-bottom: 6px;
  font-size: 14px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 4px;
  color: #333;
}
.actions-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}
.btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}
.btn-success {
  background-color: #27ae60;
  color: #fff;
}
.btn-success:hover {
  background-color: #1e8449;
}
.btn-warning {
  background-color: #f39c12;
  color: #fff;
}
.btn-warning:hover {
  background-color: #d68910;
}
.btn-danger {
  background-color: #c0392b;
  color: #fff;
}
.btn-danger:hover {
  background-color: #a93226;
}
.btn-secondary {
  background-color: #95a5a6;
  color: #fff;
}
.btn-secondary:hover {
  background-color: #7f8c8d;
}
.messages-section {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 20px;
}
.messages-section h4 {
  margin-bottom: 8px;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 1px solid #ddd;
  padding-bottom: 4px;
  text-align: left;
  color: #333;
}
.message-icon {
  margin-left: 6px;
  color: #e74c3c;
  font-size: 14px;
}
.chat-message {
  margin-bottom: 8px;
  padding: 8px;
  background: #f0f0f0;
  border-radius: 4px;
}
.chat-header {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #555;
  margin-bottom: 4px;
}
.chat-body {
  font-size: 12px;
  color: #333;
}
.send-message {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}
.send-message textarea {
  flex: 1;
  padding: 6px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 12px;
}
.send-message button {
  padding: 6px 12px;
  font-size: 12px;
  border: none;
  border-radius: 4px;
  background-color: #3498db;
  color: #fff;
  cursor: pointer;
}
.product-section {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}
.product-info,
.associated-products {
  flex: 1;
  background: #fafafa;
  padding: 10px;
  border-radius: 6px;
  text-align: center;
  border: 1px solid #eee;
}
.product-info h4,
.associated-products h4 {
  margin-bottom: 8px;
  font-size: 14px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 4px;
  text-align: center;
  color: #333;
}
.product-img-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}
.product-img {
  width: 150px;
  height: 150px;
  object-fit: contain;
  border: 1px solid #ddd;
  border-radius: 4px;
}
.assoc-grid {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
}
.assoc-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  width: 130px;
  font-size: 12px;
  text-align: center;
}
.assoc-img {
  width: 100%;
  height: 40px;
  object-fit: contain;
  margin-bottom: 4px;
}
/* Card extra para associação */
.assoc-add-card {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f7f7f7;
  border: 2px dashed #ccc;
}
/* Botão de remoção */
.btn-remove {
  margin-top: 5px;
  font-size: 10px;
  padding: 2px 4px;
}
.logs-section {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px;
}
.logs-section h4 {
  margin-bottom: 8px;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 1px solid #ddd;
  padding-bottom: 4px;
  text-align: left;
  color: #333;
}
.log-card {
  background: #ffffff;
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.log-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}
.log-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.log-user,
.log-date {
  font-size: 14px;
  color: #333;
}
.log-details div {
  font-size: 14px;
  color: #555;
}
.older-requests {
  margin-top: 15px;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #f9f9f9;
  text-align: left;
}
.older-requests h5 {
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
}
.older-request-card {
  margin-bottom: 8px;
  padding: 5px;
  border-bottom: 1px solid #ccc;
  font-size: 12px;
  color: #555;
}
.urgent-indicator {
  color: #e74c3c;
  font-size: 12px;
  background: #fff;
  border: 1px solid #e74c3c;
  padding: 2px 5px;
  border-radius: 4px;
  margin-left: 8px;
}
/* Day Counter */
.badge-age {
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-block;
}

.badge-neutral {
  background-color: #95a5a6; /* cinza neutro */
  color: #fff;
}

.age-fresh {
  background-color: #1abc9c;
  color: #000;
} /* <1 dia = cinza claro */
.age-purple {
  background-color: #8e44ad;
  color: #fff;
} /* 1–2 dias = roxo */
.age-yellow {
  background-color: #f1c40f;
  color: #000;
} /* 3 dias = amarelo */
.age-orange {
  background-color: #e67e22;
  color: #fff;
} /* 4 dias = laranja */
.age-red {
  background-color: #e74c3c;
  color: #fff;
} /* 5 dias = vermelho */
.age-black {
  background-color: #000000;
  color: #fff;
} /* >5 dias = preto */

.clickable {
  cursor: pointer;
  transition: box-shadow 0.2s ease;
}
.clickable:hover {
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
}

.offer-modal-section {
  margin-bottom: 15px;
}
.associated-products-list {
  list-style: none;
  padding-left: 0;
}

.assoc-cards-grid {
  display: flex;
  gap: 15px; /* Espaço entre os cards */
  flex-wrap: wrap; /* Permite quebrar para a próxima linha, se necessário */
  justify-content: flex-start;
}

.assoc-card {
  flex: 0 0 140px; /* Largura fixa, por exemplo 140px (ajuste conforme necessidade) */
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 8px;
  text-align: center;
  background-color: #fff;
}

.assoc-card img.assoc-img {
  width: 100%;
  height: 80px; /* Altura fixa para as imagens */
  object-fit: contain;
  margin-bottom: 5px;
}

.fixed-img {
  width: 150px; /* Ajuste a largura conforme necessário */
  height: 150px; /* Ajuste a altura conforme necessário */
  object-fit: contain; /* Mantém a proporção da imagem sem distorcer */
}
.offer-modal-top {
  margin-bottom: 1rem;
}

.left-column,
.right-column {
  /* Ajuste margens, padding etc., conforme desejar */
  margin-bottom: 1rem;
}

/* Se quiser remover mais espaços, basta ajustar os .mb-2, .mb-3, etc. */

/* Exemplo para fixar tamanho de imagem nos produtos associados */
.fixed-img {
  width: 80px;
  height: 80px;
  object-fit: contain;
}

.loading-inline {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}
.skeleton-card {
  height: 120px;
  background: #f4f4f4;
  border-radius: 8px;
  position: relative;
  animation: pulse 1.5s infinite;
  display: flex;
  align-items: center;
  justify-content: center;
}

.skeleton-spinner {
  border: 4px solid #eee;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
}

@keyframes pulse {
  0% {
    background-color: #f4f4f4;
  }
  50% {
    background-color: #eaeaea;
  }
  100% {
    background-color: #f4f4f4;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Optional loading text style */
.loading-text {
  font-size: 1.2rem;
  color: #555;
}

.header-left a img {
  cursor: zoom-in;
}

.product-img-wrapper a img {
  cursor: zoom-in;
}

.product-box {
  border: 1px solid #ddd;
  padding: 16px;
  border-radius: 8px;
  min-height: 100%;
  background-color: #f9f9f9;
}

.product-image {
  width: 100px;
  height: 100px;
  object-fit: contain;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
}

.product-placeholder {
  width: 100px;
  height: 100px;
  border: 1px dashed #ccc;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #888;
  font-size: 14px;
  background-color: #fff;
}

.product-link {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

.product-link:hover {
  text-decoration: underline;
}

/*  */

.product-image,
.product-placeholder {
  width: 100px;
  height: 100px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #ccc;
}

.product-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
  font-size: 13px;
  color: #888;
}

.product-link {
  color: #007bff;
  text-decoration: none;
  font-weight: 500;
}

.product-link:hover {
  text-decoration: underline;
}

.assoc-cards-grid {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.assoc-card {
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 6px;
  width: 180px;
  background-color: #fff;
}

.assoc-img {
  width: 100%;
  height: 100px;
  object-fit: contain;
  border-radius: 4px;
  border: 1px solid #ccc;
  margin-bottom: 8px;
}

.image-wrapper {
  position: relative;
  display: inline-block;
}

.image-id-overlay {
  position: absolute;
  top: -8px;
  left: -8px;
  background-color: #3498db;
  color: white;
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: bold;
  z-index: 2;
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.2);
}

.badge-type {
  margin-left: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.type-normal {
  background-color: #3498db;
  color: white;
}

.type-fba {
  background-color: #27ae60;
  color: white;
}

.request-id {
  font-weight: bold;
  font-size: 12px;
  color: #2c3e50;
  background-color: #ecf0f1;
  border: 1px solid #bdc3c7;
  border-radius: 4px;
  padding: 4px 8px;
  margin-right: 10px;
}

.full-filter-bar {
  display: flex;
  gap: 15px;
  align-items: flex-end;
  flex-wrap: wrap;
}

.filter-wrapper {
  display: flex;
  flex-direction: column;
}

.sort-controls {
  display: flex;
  gap: 15px;
  align-items: flex-end;
}

.sort-wrapper {
  display: flex;
  flex-direction: column;
}

.sort-label {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
  color: #444;
}

.sort-wrapper select {
  padding: 8px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  background-color: white;
  font-size: 14px;
  color: #333;
}

.sort-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.sort-toggle-btn:hover {
  background-color: #f0f0f0;
}

.sort-toggle-btn i {
  font-size: 14px;
  transition: transform 0.3s ease;
}

.direction {
  display: flex;
  align-items: center;
}

.button-group {
  display: flex;
  gap: 10px;
}

.conversion-wrapper {
  /* Se quiseres afastar um pouco dos outros filtros: */
  margin-left: 1rem;
}

.conversion-wrapper label {
  white-space: nowrap;
}

.conversion-wrapper input {
  width: 80px;
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 0.9rem;
  background: #fff;
  transition: border-color 0.2s;
}

.conversion-wrapper input:focus {
  outline: none;
  border-color: #888;
}

.product-name-link {
  color: inherit; /* mantém a cor do texto original */
  text-decoration: none;
  font-weight: 500;
}

.product-name-link:hover {
  text-decoration: underline;
}

.shipments-section {
  border: 1px solid #eee;
  border-radius: 6px;
  padding: 10px;
  margin-bottom: 20px;
}

.shipments-section h4 {
  margin-bottom: 8px;
  font-size: 14px;
  cursor: pointer;
  border-bottom: 1px solid #ddd;
  padding-bottom: 4px;
  text-align: left;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.shipment-icon {
  color: #3498db;
}

.shipment-count {
  font-size: 13px;
  color: #666;
}

.shipment-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.shipment-card {
  background: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 8px;
  font-size: 13px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.shipment-card p {
  margin-bottom: 4px;
  color: #555;
}

.shipment-card a {
  color: #2980b9;
  text-decoration: none;
}

.shipment-card a:hover {
  text-decoration: underline;
}

#offerModal {
  font-family: 'Segoe UI', Roboto, sans-serif;
}
#offerModal .offer-grid {
  display: grid;
  grid-template-columns: 300px 1fr;
  grid-auto-rows: auto;
  gap: 1rem;
}
#offerModal .card {
  border: none;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #fff;
}
#offerModal .card-header {
  background: transparent;
  border-bottom: none;
  padding-bottom: 0;
}
#offerModal .card-title {
  font-size: 1rem;
  color: #333;
}
#offerModal .card-body {
  padding-top: 0.5rem;
}
#offerModal .avatar-container {
  flex-shrink: 0;
}
#offerModal .avatar {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  object-fit: cover;
}
#offerModal .avatar-placeholder {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 1.25rem;
}
#offerModal .assoc-card {
  /* stretch card to use full right-side space */
  grid-column: 2 / -1;
  width: 100%;
  justify-self: stretch;
  align-self: start;
}
#offerModal .assoc-body {
  overflow-x: auto;
}
#offerModal .assoc-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.75rem;
  width: 100%;
}
#offerModal .assoc-thumb {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
}
#offerModal .assoc-item {
  flex: 0 0 auto;
  width: 180px;
}
#offerModal .full-card {
  grid-column: 1 / -1;
}
#offerModal .older-item {
  min-width: 120px;
  background: #f8f9fa;
  border-radius: 6px;
}
#offerModal .font-monospace {
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
}
#offerModal .input-group-text {
  background: #fff;
  border: none;
}

@media (max-width: 767px) {
  .top-info {
    flex-direction: column;
  }
  .product-section {
    flex-direction: column;
  }
}
</style>
